import {
  useCallback,
  useEffect,
  useRef,
  useState,
  useSyncExternalStore,
  type FocusEvent as ReactFocusEvent,
  type MouseEvent as ReactMouseEvent,
  type PointerEvent as ReactPointerEvent,
} from 'react';

/**
 * SANFONA DO COCKPIT — zonas da ficha que ficam recolhidas (só o essencial)
 * e abrem quando o mouse PARA em cima. Mesmo truque dos cartões do Google
 * Workspace Studio, com as regras de consultório do estudo de 09/10/2026:
 *   - só abre depois de ~200 ms parado em cima (passar de raspão não mexe);
 *   - onde se está digitando nunca encolhe: aberta durante a digitação, a
 *     zona FLUTUA por cima em vez de empurrar o editor;
 *   - digitando dentro da zona (busca, título) ou com foco de teclado nela,
 *     ela segura aberta; clicar fora ou Esc recolhe;
 *   - ESCOLHER algo nela (um registro, um destino do menu) não recolhe:
 *     com o mouse em cima ela fica; recolhe quando o mouse sai;
 *   - em tela de toque abre no toque, sem espera.
 *
 * Da pessoa (segue a conta, `prefsNaConta`) e DESLIGADA por padrão: Configurações › Tela de Atendimento ›
 * "Painéis que abrem ao passar o mouse" (grava `dratento.cockpit.sanfona = 1`).
 * Desligada, cada tela fica exatamente como era.
 */
export const COCKPIT_SANFONA_KEY = 'dratento.cockpit.sanfona';

const CHANGE_EVENT = 'dratento:sanfona';
const readEnabled = () => {
  try {
    return localStorage.getItem(COCKPIT_SANFONA_KEY) === '1';
  } catch {
    return false;
  }
};
const subscribeEnabled = (cb: () => void) => {
  window.addEventListener(CHANGE_EVENT, cb);
  window.addEventListener('storage', cb);
  return () => {
    window.removeEventListener(CHANGE_EVENT, cb);
    window.removeEventListener('storage', cb);
  };
};

/** Liga/desliga ao vivo: quem usa o hook atualiza na hora, sem recarregar. */
export const useCockpitSanfonaEnabled = (): boolean =>
  useSyncExternalStore(subscribeEnabled, readEnabled, () => false);

/** Chave de Configurações › Tela de Atendimento (segue a conta). */
export const setCockpitSanfonaEnabled = (on: boolean) => {
  try {
    localStorage.setItem(COCKPIT_SANFONA_KEY, on ? '1' : '0');
  } catch {
    /* sem storage: fica como está */
  }
  window.dispatchEvent(new Event(CHANGE_EVENT));
};

/** Há um campo de digitação com foco FORA da zona? (aí a zona flutua) */
const typingOutside = (zone: HTMLElement | null) => {
  const a = document.activeElement as HTMLElement | null;
  if (!a || a === document.body || (zone && zone.contains(a))) return false;
  return a.isContentEditable || a.matches('input, textarea, select');
};

/**
 * Depois de ESCOLHER algo na zona (registro, destino do menu, paciente): a
 * zona fica aberta enquanto o mouse estiver em cima, mas o botão clicado não
 * pode segurá-la depois que o mouse sai — senão o painel ficaria cobrindo o
 * que acabou de abrir até um clique fora. Solta o foco do clique de MOUSE;
 * foco de teclado (Tab + Enter) continua onde está.
 */
export const releaseClickFocus = (zone: HTMLElement | null) => {
  const a = document.activeElement as HTMLElement | null;
  if (a && zone?.contains(a) && !a.matches(':focus-visible')) a.blur();
};

interface SanfonaOptions {
  openDelay?: number;
  closeDelay?: number;
  /** Seletor de pedaços da zona que NÃO abrem a sanfona ao parar o mouse
   *  (ex.: o campo de ditado no cabeçalho recolhido — parar no "Ditar" é
   *  pra clicar nele, não pra abrir a espiada por cima dele). */
  ignore?: string;
}

/** Abre/fecha por intenção de hover + foco. `float` é decidido na abertura. */
export const useSanfona = ({ openDelay = 200, closeDelay = 380, ignore }: SanfonaOptions = {}) => {
  const ref = useRef<HTMLDivElement | null>(null);
  const [open, setOpen] = useState(false);
  const [float, setFloat] = useState(false);
  const openRef = useRef(false);
  const hovering = useRef(false);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  // Logo depois de abrir, o conteúdo completo ainda está entrando por baixo
  // do dedo/mouse: um clique nesse intervalo cairia num botão que a pessoa
  // não viu. Engolido.
  const guardUntil = useRef(0);
  // A abertura foi cancelada por um pedaço ignorado (e só por isso): voltar
  // pro resto da zona arma de novo. Esc não conta — só reabre em nova entrada.
  const suppressed = useRef(false);

  const clear = () => {
    if (timer.current) clearTimeout(timer.current);
    timer.current = null;
  };
  const set = useCallback((v: boolean) => {
    if (v && !openRef.current) {
      setFloat(typingOutside(ref.current));
      guardUntil.current = Date.now() + 300;
    }
    openRef.current = v;
    setOpen(v);
  }, []);
  /** Segura aberta: campo de digitação com foco dentro, ou foco de TECLADO
   *  dentro. Botão clicado com o mouse não segura — senão, depois de adicionar
   *  um módulo, o painel ficava aberto cobrindo os campos. */
  const holdsFocus = () => {
    const a = document.activeElement as HTMLElement | null;
    if (!a || !ref.current?.contains(a)) return false;
    return a.isContentEditable || a.matches('input, textarea, select') || a.matches(':focus-visible');
  };
  const closeIfIdle = () => {
    clear();
    timer.current = setTimeout(() => {
      if (hovering.current || holdsFocus()) return;
      set(false);
    }, closeDelay);
  };
  /** Fecha já (Esc). Só reabre numa NOVA entrada do mouse. */
  const close = useCallback(() => {
    clear();
    set(false);
    const a = document.activeElement as HTMLElement | null;
    if (a && ref.current?.contains(a)) a.blur();
  }, [set]);
  /** Escolheu algo nela: segue aberta com o mouse em cima, recolhe quando ele sai. */
  const release = useCallback(() => releaseClickFocus(ref.current), []);

  useEffect(() => () => clear(), []);
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') close();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open, close]);

  const ignored = (t: EventTarget | null) =>
    !!ignore && t instanceof Element && !!t.closest(ignore);
  const schedule = (e: ReactPointerEvent) => {
    if (e.pointerType === 'touch') set(true);
    else timer.current = setTimeout(() => set(true), openDelay);
  };

  const bind = {
    ref,
    onPointerEnter: (e: ReactPointerEvent) => {
      hovering.current = true;
      clear();
      suppressed.current = false;
      if (openRef.current) return;
      if (ignored(e.target)) suppressed.current = true;
      else schedule(e);
    },
    // Andando DENTRO da zona: entrar num pedaço ignorado cancela a abertura;
    // voltar pro resto dela arma de novo.
    onPointerOver: (e: ReactPointerEvent) => {
      if (!ignore || openRef.current) return;
      if (ignored(e.target)) {
        if (timer.current) suppressed.current = true;
        clear();
      } else if (suppressed.current) {
        suppressed.current = false;
        schedule(e);
      }
    },
    onPointerLeave: () => {
      hovering.current = false;
      closeIfIdle();
    },
    // Só o foco de TECLADO abre (Tab até a zona). O clique do mouse num botão
    // da faixa recolhida faz o que o botão diz — não é pedido pra abrir.
    onFocus: (e: ReactFocusEvent) => {
      clear();
      if (!openRef.current && (e.target as HTMLElement).matches(':focus-visible')) set(true);
    },
    onBlur: (e: ReactFocusEvent) => {
      if (ref.current?.contains(e.relatedTarget as Node | null)) return;
      closeIfIdle();
    },
    onClickCapture: (e: ReactMouseEvent) => {
      if (Date.now() < guardUntil.current) {
        e.preventDefault();
        e.stopPropagation();
      }
    },
  };

  return { open, float, bind, close, release };
};
